import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { Alert, StackView } from '@planningcenter/tapestry-react'
import {
  Code,
  CodeBlock as TypographyCodeBlock,
  Heading,
  Link as SharedLink,
  List,
  PageHeading,
  Text,
} from '../../../shared/typography'

export default function page() {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Proof Key for Code Exchange (PKCE)</PageHeading>

      <Text>
        Planning Center supports the{' '}
        <Link external to="https://datatracker.ietf.org/doc/html/rfc7636">
          OAuth 2.0 PKCE extension
        </Link>{' '}
        for both confidential and public OAuth applications. PKCE adds
        parameters to the OAuth flow that allows the OAuth server (Planning
        Center in this case) to ensure the OAuth application that starts the
        flow is the same one that finishes it.
      </Text>

      <Text>
        It's recommended for all applications and is required by{' '}
        <Link external to="https://oauth.net/2.1/">
          OAuth 2.1
        </Link>
        . Currently, we recommend PKCE for confidential apps and require it for
        public apps.
      </Text>

      <Heading anchored id="oauth-application-types" size={1}>
        OAuth Application Types
      </Heading>

      <Text>
        OAuth defines two types of applications: confidential and public.
      </Text>

      <Text>
        Confidential applications are able to keep their registered{' '}
        <Code>client_secret</Code> safe and use it to authenticate with the
        authorization server (Planning Center). Generally, that requires
        requests for access tokens to originate from a web server you own.
      </Text>

      <Text>
        Public applications are unable to use a registered{' '}
        <Code>client_secret</Code>, usually because they're running in the
        browser or on a mobile device.
      </Text>

      <Alert type="warning" marginBottom={2}>
        <Text size={4}>
          <strong>Important:</strong> We recommend you create a confidential
          OAuth application, you issue authorization requests from a server
          where you can keep your <Code>client_secret</Code> safe, and you
          utilize the PKCE extension. This is the most secure setup for both you
          and our shared users.
        </Text>
      </Alert>

      <Heading anchored id="oidc-workflow" size={1}>
        PKCE Workflow
      </Heading>

      <List ordered>
        <List.Item>
          <Text>
            Generate a <Code>code_verifier</Code> and derive a{' '}
            <Code>code_challenge</Code> using SHA-256. Per RFC 7636, the{' '}
            <Code>code_verifier</Code> is a high-entropy random string using
            unreserved URL characters, 43-128 characters long.
          </Text>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              Redirect the user to the authorization endpoint with the normal
              parameters plus <Code>code_challenge</Code> and{' '}
              <Code>code_challenge_method=S256</Code>
            </Text>

            <CodeBlock>
              {`
https://api.planningcenteronline.com/oauth/authorize?client_id=CLIENT_ID \\
  &code_challenge=CODE_CHALLENGE \\
  &code_challenge_method=S256
  &redirect_uri=https://example.com/auth/complete \\
  &response_type=code \\
  &scope=people
              `.trim()}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <Text>
            Planning Center will redirect the user’s browser back to the given
            redirect URI with a <Code>code</Code> param.
          </Text>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              Send a POST request in the background to{' '}
              <Code>https://api.planningcenteronline.com/oauth/token</Code> with
              the normal params as well as the original{' '}
              <Code>code_verifier</Code> from step 1:
            </Text>

            <CodeBlock language="bash">
              {`
curl -X POST https://api.planningcenteronline.com/oauth/token \\
  -F grant_type=authorization_code \\
  -F code=1234567890 \\
  -F code_verifier=CODE_VERIFIER_FROM_STEP_1 \\
  -F client_id=2345678901 \\
  -F client_secret=3456789012 \\
  -F redirect_uri=https://example.com/auth/complete
            `.trim()}
            </CodeBlock>
          </StackView>
        </List.Item>
      </List>
    </StackView>
  )
}

function Link({ external, ...props }) {
  return external ? (
    <SharedLink external {...props} />
  ) : (
    <ReactRouterLink component={SharedLink} {...props} />
  )
}

function CodeBlock(props) {
  return (
    <TypographyCodeBlock
      customStyle={{ marginTop: 0, marginBottom: 0 }}
      {...props}
    />
  )
}
